import { useAuth, authorizedFetch } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type ConditionClinicalStatus = 'active' | 'monitoring' | 'remission' | 'resolved'

export interface ConditionItem {
  id: string
  name: string
  condition_type?: string | null
  clinical_status: ConditionClinicalStatus
  verification_status: string
  onset_date?: string | null
  abatement_date?: string | null
  notes?: string
  source?: string
  created_at: string
  updated_at: string
}

export interface ConditionRelationItem {
  id: string
  condition_id: string
  target_type: string
  target_id: string
  target_summary?: string | null
  relation_type: string
  discovery_method: string
  created_at: string
}

export interface ConditionFilters {
  clinical_status?: ConditionClinicalStatus
  limit?: number
}

export interface CreateConditionData {
  name: string
  clinical_status?: ConditionClinicalStatus
  onset_date?: string
  notes?: string
}

export interface UpdateConditionData {
  condition_id: string
  name?: string
  clinical_status?: ConditionClinicalStatus
  onset_date?: string | null
  abatement_date?: string | null
  notes?: string
}

export function useConditions(filters: ConditionFilters = {}) {
  const { token } = useAuth()
  const queryKey = ['conditions', filters]

  const q = useQuery({
    queryKey,
    queryFn: async (): Promise<ConditionItem[]> => {
      if (!token) throw new Error('Not authenticated')
      const params = new URLSearchParams()
      if (filters.clinical_status) params.append('clinical_status', filters.clinical_status)
      if (filters.limit) params.append('limit', String(filters.limit))
      const qs = params.toString()
      const response = await authorizedFetch(`/api/conditions/${qs ? `?${qs}` : ''}`)
      if (!response.ok) throw new Error('Failed to fetch conditions')
      return response.json()
    },
    enabled: !!token,
    staleTime: 30 * 1000,
  })

  return {
    conditions: q.data ?? [],
    isLoading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  }
}

export function useCondition(conditionId: string | undefined) {
  const { token } = useAuth()

  const q = useQuery({
    queryKey: ['conditions', 'detail', conditionId],
    queryFn: async (): Promise<ConditionItem> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`/api/conditions/${conditionId}`)
      if (!response.ok) throw new Error('Failed to fetch condition')
      return response.json()
    },
    enabled: !!token && !!conditionId,
    staleTime: 30 * 1000,
  })

  return {
    condition: q.data ?? null,
    isLoading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  }
}

export function useConditionRelations(conditionId: string | undefined) {
  const { token } = useAuth()

  const q = useQuery({
    queryKey: ['conditions', 'relations', conditionId],
    queryFn: async (): Promise<ConditionRelationItem[]> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`/api/conditions/${conditionId}/relations`)
      if (!response.ok) throw new Error('Failed to fetch condition links')
      return response.json()
    },
    enabled: !!token && !!conditionId,
    staleTime: 30 * 1000,
  })

  return {
    relations: q.data ?? [],
    isLoading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  }
}

export function useCreateCondition() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateConditionData): Promise<ConditionItem> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch('/api/conditions/', {
        method: 'POST',
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error((errorData as { error?: string }).error || 'Failed to create condition')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conditions'] })
    },
  })
}

export function useUpdateCondition() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: UpdateConditionData): Promise<ConditionItem> => {
      if (!token) throw new Error('Not authenticated')
      const { condition_id, ...body } = data
      const response = await authorizedFetch(`/api/conditions/${condition_id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error((errorData as { error?: string }).error || 'Failed to update condition')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conditions'] })
    },
  })
}

export function useDeleteCondition() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (conditionId: string): Promise<void> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch(`/api/conditions/${conditionId}`, { method: 'DELETE' })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error((errorData as { error?: string }).error || 'Failed to delete condition')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conditions'] })
    },
  })
}
